import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Eye, Trash2, CheckCircle, Package, Hash, Pencil, Info } from 'lucide-react';
import { raceTypesAPI } from '@/services/raceTypesApi';
import { discountsAPI } from '@/services/discountsApi';
import { useRegistrations } from '@/hooks/useRegistrations';
import { useRaces } from '@/hooks/useRaces';
import { StatusBadge } from '@/components/shared';
import { useCSVExport } from '@/hooks/useCSVExport';
import {
  BIB_NUMBERING_SCOPE_SHARED_RACE_POOL,
  getBibNumberingModeDescription,
  getBibNumberingModeLabel,
  normalizeBibNumberingConfig,
} from '@/utils/bibNumbering';
import { useRegistrationsActions } from './useRegistrationsActions';
import RegistrationsManagerView from './registrationsmanager.view';

const RegistrationsManager: React.FC<{ raceId?: string }> = ({ raceId: raceIdProp }) => {
  const isEmbedded = !!raceIdProp;
  const { data: registrations = [], isLoading: loadingRegs } = useRegistrations();
  const { data: races = [], isLoading: loadingRaces } = useRaces();

  const { data: raceTypesMaps = { types: {}, prices: {}, details: {}, byRace: {} } } = useQuery({
    queryKey: ['raceTypesAll', races.map((r: any) => r.id)],
    queryFn: async () => {
      const typesMap: Record<string, string> = {};
      const pricesMap: Record<string, number> = {};
      const detailsMap: Record<string, any> = {};
      const byRaceMap: Record<string, any[]> = {};

      await Promise.all(
        races.map(async (race: any) => {
          try {
            const types = await raceTypesAPI.getRaceTypes(race.id);
            byRaceMap[race.id] = types;
            types.forEach((type: any) => {
              typesMap[type.id] = type.name;
              pricesMap[type.id] = type.price || 0;
              detailsMap[type.id] = type;
            });
          } catch (error) {
            console.error('Failed to fetch race types for race', race.id, error);
          }
        })
      );

      return { types: typesMap, prices: pricesMap, details: detailsMap, byRace: byRaceMap };
    },
    enabled: races.length > 0,
  });
  const raceTypes = raceTypesMaps.types;
  const raceTypePrices = raceTypesMaps.prices;
  const raceTypeDetails = raceTypesMaps.details;
  const raceTypesByRace = raceTypesMaps.byRace;

  const { data: discountsByRace = {} } = useQuery({
    queryKey: ['discountsAll', races.map((r: any) => r.id)],
    queryFn: async () => {
      const discountsMap: Record<string, any[]> = {};
      await Promise.all(
        races.map(async (race: any) => {
          try {
            discountsMap[race.id] = await discountsAPI.getDiscounts(race.id);
          } catch {
            discountsMap[race.id] = [];
          }
        })
      );
      return discountsMap;
    },
    enabled: races.length > 0,
  });

  const actions = useRegistrationsActions(raceTypeDetails);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterRace, setFilterRace] = useState(raceIdProp || '');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [filterRaceType, setFilterRaceType] = useState('');

  const { exportCSV } = useCSVExport();
  const loading = loadingRegs || loadingRaces;

  const raceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    races.forEach((race: any) => {
      map[race.id] = race.name;
    });
    return map;
  }, [races]);

  const getRaceName = useCallback((raceId: any) => raceNameMap[raceId] || 'N/A', [raceNameMap]);
  const getRaceTypeName = useCallback((raceTypeId: any) => raceTypes[raceTypeId] || 'N/A', [raceTypes]);
  const getRaceTypeDetails = useCallback((raceTypeId: any) => raceTypeDetails[raceTypeId] || null, [raceTypeDetails]);

  const getDiscountInfo = useCallback((reg: any) => {
    if (!reg.discountCode) return { discountText: null, discountAmount: 0 };
    const raceDiscounts = discountsByRace[reg.raceId] || [];
    const discount = raceDiscounts.find((d: any) => d.code.toUpperCase() === reg.discountCode.toUpperCase());
    if (!discount) return { discountText: reg.discountCode, discountAmount: 0 };
    const price = raceTypePrices[reg.raceTypeId] || 0;
    if (discount.discountPercentage) {
      const amt = Math.round(price * discount.discountPercentage / 100);
      return { discountText: discount.discountPercentage + '%', discountAmount: amt };
    }
    if (discount.discountAmount) return { discountText: discount.discountAmount + ' MKD', discountAmount: discount.discountAmount };
    return { discountText: reg.discountCode, discountAmount: 0 };
  }, [discountsByRace, raceTypePrices]);

  const getFinalPrice = useCallback((reg: any) => {
    const price = raceTypePrices[reg.raceTypeId] || 0;
    const { discountAmount } = getDiscountInfo(reg);
    return Math.max(0, price - discountAmount);
  }, [getDiscountInfo, raceTypePrices]);

  const filteredRegistrations = registrations.filter((reg: any) => {
    const normalizedQuery = searchQuery.toLowerCase();
    const bibSearch = reg.bibNumber != null ? String(reg.bibNumber) : '';
    const matchesSearch =
      reg.firstName.toLowerCase().includes(normalizedQuery) ||
      reg.lastName.toLowerCase().includes(normalizedQuery) ||
      reg.email.toLowerCase().includes(normalizedQuery) ||
      bibSearch.includes(searchQuery);
    const matchesRace = !filterRace || reg.raceId.toString() === filterRace;
    const matchesRaceType = !filterRaceType || reg.raceTypeId.toString() === filterRaceType;
    const matchesStatus = !filterStatus || reg.registrationStatus === filterStatus;
    const matchesPayment = !filterPayment || reg.paymentStatus === filterPayment;
    return matchesSearch && matchesRace && matchesRaceType && matchesStatus && matchesPayment;
  });

  const handleExportCSV = () => {
    exportCSV(filteredRegistrations, [
      { key: 'bibNumber', header: 'BibNumber' },
      { key: 'firstName', header: 'FirstName' },
      { key: 'lastName', header: 'LastName' },
      { key: 'email', header: 'Email' },
      { key: 'phone', header: 'Phone' },
      { key: 'city', header: 'City' },
      { key: 'country', header: 'Country' },
      { key: 'gender', header: 'Gender' },
      { key: 'dateOfBirth', header: 'DateOfBirth' },
      { key: 'participantType', header: 'ParticipantType' },
      { key: 'teamName', header: 'TeamName' },
      { key: 'tshirtSize', header: 'TshirtSize' },
      { key: 'emergencyContactPhone', header: 'EmergencyContactPhone' },
      { key: 'cookiesConsent', header: 'CookiesConsent' },
      { key: 'privacyPolicy', header: 'PrivacyPolicy' },
      { key: 'transactionSuccessful', header: 'TransactionSuccessful' },
      { key: 'ip', header: 'IP' },
      { key: 'userAgent', header: 'UserAgent' },
      { key: 'raceId', header: 'RaceId' },
      { key: 'raceTypeId', header: 'RaceTypeId' },
      { key: 'raceId', header: 'Race', format: (reg: any) => getRaceName(reg.raceId) },
      { key: 'raceTypeId', header: 'RaceType', format: (reg: any) => getRaceTypeName(reg.raceTypeId) },
      { key: 'discountCode', header: 'DiscountCode', format: (reg: any) => reg.discountCode || '' },
      { key: 'registrationStatus', header: 'RegistrationStatus' },
      { key: 'paymentStatus', header: 'PaymentStatus' },
      { key: 'bibPickedUp', header: 'BibPickedUp', format: (reg: any) => reg.bibPickedUp ? 'Yes' : 'No' },
      { key: 'createdAt', header: 'CreatedAt' },
    ], 'registrations');
  };

  const stats = useMemo(() => ({
    total: registrations.length,
    confirmed: registrations.filter((r: any) => r.registrationStatus === 'Confirmed').length,
    pending: registrations.filter((r: any) => r.registrationStatus === 'Pending').length,
    paid: registrations.filter((r: any) => r.paymentStatus === 'Paid').length,
    unpaid: registrations.filter((r: any) => r.paymentStatus === 'Pending').length,
  }), [registrations]);

  const columns = useMemo(() => [
    {
      key: 'bibNumber',
      label: 'Стартен број',
      cellClassName: 'whitespace-nowrap font-medium text-gray-900',
      render: (v: any, reg: any) => {
        const numbering = normalizeBibNumberingConfig(getRaceTypeDetails(reg.raceTypeId));

        if (!numbering.bibNumberingEnabled) {
          return (
            <div>
              <div>{v != null ? '#' + v : 'Без број'}</div>
              <div className="text-xs text-gray-500">Нумерацијата е исклучена</div>
            </div>
          );
        }

        if (v != null) {
          return (
            <div>
              <div>#{v}</div>
              <div className="text-xs text-gray-500">
                {numbering.bibNumberingScope === BIB_NUMBERING_SCOPE_SHARED_RACE_POOL
                  ? 'Pool: ' + (numbering.bibNumberingPoolKey || 'shared')
                  : 'Одделен опсег'}
              </div>
            </div>
          );
        }

        return (
          <div>
            <div className="text-gray-400">{reg.registrationStatus === 'Confirmed' ? 'Недоделен' : 'По потврда'}</div>
            <div className="text-xs text-gray-500">{getBibNumberingModeLabel(numbering)}</div>
          </div>
        );
      },
    },
    {
      key: 'firstName',
      label: 'Име',
      cellClassName: 'whitespace-nowrap text-gray-900',
      render: (_: any, reg: any) => (
        <div>
          <div>{reg.firstName} {reg.lastName}</div>
          <div className="text-xs text-gray-500">{reg.email}</div>
        </div>
      ),
    },
    { key: 'teamName', label: 'Тим', cellClassName: 'whitespace-nowrap text-gray-600 text-sm', render: (v: any) => v || <span className="text-gray-400">—</span> },
    { key: 'tshirtSize', label: 'Маичка', cellClassName: 'whitespace-nowrap text-gray-600 text-sm', render: (v: any) => v ? <span className="bg-gray-100 px-2 py-1 rounded text-xs font-semibold">{v}</span> : <span className="text-gray-400">—</span> },
    {
      key: 'raceId',
      label: 'Трка / Тип',
      cellClassName: 'text-gray-600 text-sm',
      render: (_: any, reg: any) => {
        const raceType = getRaceTypeDetails(reg.raceTypeId);
        const numbering = normalizeBibNumberingConfig(raceType);

        const description = getBibNumberingModeDescription(numbering);
        return (
          <div>
            <div>{getRaceName(reg.raceId)}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              {getRaceTypeName(reg.raceTypeId)}
              {description && (
                <span className="relative group inline-flex flex-shrink-0">
                  <Info className="w-3 h-3 text-muted-foreground cursor-pointer" />
                  <span className="absolute z-50 bottom-full left-0 mb-1 w-48 rounded-md bg-popover text-popover-foreground border border-border shadow-md px-2 py-1 text-xs hidden group-hover:block whitespace-normal">
                    {description}
                  </span>
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'discountCode',
      label: 'Попуст',
      cellClassName: 'whitespace-nowrap text-gray-600 text-sm',
      render: (v: any, reg: any) => {
        if (!v) return <span className="text-gray-400">—</span>;
        const { discountText } = getDiscountInfo(reg);
        return <div><span className="font-mono text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">{v}</span>{discountText && <div className="text-xs text-purple-600 mt-1">-{discountText}</div>}</div>;
      },
    },
    {
      key: '_price',
      label: 'Цена',
      cellClassName: 'whitespace-nowrap text-sm',
      render: (_: any, reg: any) => {
        const originalPrice = raceTypePrices[reg.raceTypeId] || 0;
        const finalPrice = getFinalPrice(reg);
        return reg.discountCode && finalPrice < originalPrice
          ? <div><span className="text-gray-400 line-through text-xs">{originalPrice} MKD</span><div className="font-semibold text-green-700">{finalPrice} MKD</div></div>
          : <span className="font-semibold text-gray-900">{originalPrice} MKD</span>;
      },
    },
    { key: 'registrationStatus', label: 'Статус', cellClassName: 'whitespace-nowrap', render: (v: any) => <StatusBadge label={v} variant={v === 'Confirmed' ? 'success' : 'warning'} /> },
    { key: 'paymentStatus', label: 'Плаќање', cellClassName: 'whitespace-nowrap', render: (v: any) => <StatusBadge label={v} variant={v === 'Paid' ? 'info' : 'error'} /> },
    {
      key: 'bibPickedUp',
      label: 'Преземање',
      className: 'text-center',
      cellClassName: 'whitespace-nowrap text-center',
      render: (v: any, reg: any) => {
        const numbering = normalizeBibNumberingConfig(getRaceTypeDetails(reg.raceTypeId));

        if (!numbering.bibNumberingEnabled) {
          return <span className="text-gray-400">Нема број</span>;
        }
        if (reg.registrationStatus !== 'Confirmed' || reg.bibNumber == null) return <span className="text-gray-400">—</span>;
        return v ? <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-green-100 text-green-800"><Package className="w-3 h-3" />Преземено</span> : <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-800">Не</span>;
      },
    },
    {
      key: '_actions',
      label: 'Акции',
      className: 'text-right',
      cellClassName: 'whitespace-nowrap text-right space-x-2',
      render: (_: any, reg: any) => (
        <>
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); actions.handleViewDetails(reg); }} title="Детали"><Eye className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); actions.handleOpenEdit(reg); }} title="Измени регистрација"><Pencil className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); actions.handleOpenBibDialog(reg); }} title="Измени стартен број"><Hash className="w-4 h-4" /></Button>
          {reg.registrationStatus !== 'Confirmed' && (
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); actions.handleOpenConfirmDialog(reg); }} className="text-green-600 hover:text-green-700 hover:bg-green-50" title="Потврди регистрација"><CheckCircle className="w-4 h-4" /></Button>
          )}
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); actions.handleDelete(reg.id); }} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Избриши регистрација"><Trash2 className="w-4 h-4" /></Button>
        </>
      ),
    },
  ], [actions, getDiscountInfo, getFinalPrice, getRaceName, getRaceTypeDetails, getRaceTypeName, raceTypePrices]);

  return (
    <RegistrationsManagerView
      loading={loading}
      stats={stats}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterRace={filterRace}
      setFilterRace={setFilterRace}
      filterStatus={filterStatus}
      setFilterStatus={setFilterStatus}
      filterPayment={filterPayment}
      setFilterPayment={setFilterPayment}
      filterRaceType={filterRaceType}
      setFilterRaceType={setFilterRaceType}
      races={races}
      columns={columns}
      filteredRegistrations={filteredRegistrations}
      handleExportCSV={handleExportCSV}
      selectedRegistration={actions.selectedRegistration}
      isDetailDialogOpen={actions.isDetailDialogOpen}
      setIsDetailDialogOpen={actions.setIsDetailDialogOpen}
      isConfirmDialogOpen={actions.isConfirmDialogOpen}
      setIsConfirmDialogOpen={actions.setIsConfirmDialogOpen}
      confirmingRegistration={actions.confirmingRegistration}
      handleConfirm={actions.handleConfirm}
      confirmRegistrationPending={actions.confirmRegistrationPending}
      deleteTarget={actions.deleteTarget}
      setDeleteTarget={actions.setDeleteTarget}
      confirmDelete={actions.confirmDelete}
      isBibDialogOpen={actions.isBibDialogOpen}
      setIsBibDialogOpen={actions.setIsBibDialogOpen}
      editingBibRegistration={actions.editingBibRegistration}
      bibNumberInput={actions.bibNumberInput}
      setBibNumberInput={actions.setBibNumberInput}
      handleSaveBibNumber={actions.handleSaveBibNumber}
      updateBibNumberPending={actions.updateBibNumberPending}
      getRaceName={getRaceName}
      getRaceTypeName={getRaceTypeName}
      getRaceTypeDetails={getRaceTypeDetails}
      getDiscountInfo={getDiscountInfo}
      getFinalPrice={getFinalPrice}
      raceTypePrices={raceTypePrices}
      embedded={isEmbedded}
      isEditDialogOpen={actions.isEditDialogOpen}
      setIsEditDialogOpen={actions.setIsEditDialogOpen}
      editingRegistration={actions.editingRegistration}
      registrationFormData={actions.registrationFormData}
      setRegistrationFormData={actions.setRegistrationFormData}
      handleOpenCreate={actions.handleOpenCreate}
      handleCloseEditDialog={actions.handleCloseEditDialog}
      handleSubmitRegistration={actions.handleSubmitRegistration}
      raceTypes={raceTypesByRace}
    />
  );
};

export default RegistrationsManager;
